import { Displayable, Info } from '@eo-storefronts/eo-core'
import { createSelector, createState } from '~/src/hooks/useEoState'
import { IS_MARKETPLACE_SELECTOR } from '~/src/stores/brand'
import { FIRM_SELECTOR, SELECTED_SHOP_STATE } from '~/src/stores/firm'

const key = 'layoutState'

export interface LogoOptions {
  disableLink: boolean,
}

export interface HeaderState extends Displayable {
  type: 'navigation' | 'simple' | 'default',
  title: string,
  logo: LogoOptions,
  displayMenuBurger: boolean,
  displayCart: boolean,
  displayCurrentFirm: boolean,
  displayLanguagesSelector: boolean,
  displayUserActions: boolean,
  displayProductSearch: boolean,
  displayLocationSearch: boolean,
  displayDeliveryOptions: boolean,
  className?: string,
}

export interface SubHeaderState extends Displayable {
  variant: 'default' | 'small',
  className?: string,
}

export interface FooterState extends Displayable {
  displayLinks: boolean,
  displayInformations: boolean,
  displayGoToCheckoutBtn: boolean,
  displayGoToCartBtn: boolean,
}

export type MobileNavigationState = Displayable

export interface ContentState {
  displayNotification: boolean,
}

export interface MainSideBarState extends Displayable {
  displayCart: boolean,
  displayDeliveryOptions: boolean,
  displayOrderSummary: boolean,
}

export interface LayoutState {
  header: HeaderState,
  subHeader: SubHeaderState,
  mainSideBar: MainSideBarState,
  content: ContentState,
  footer: FooterState,
  mobileNavigation: MobileNavigationState,
  isMobile: boolean,
}

const initialHeaderState = (): HeaderState => (
  {
    display: false,
    type: 'default',
    title: '',
    logo: {
      disableLink: false
    },
    displayMenuBurger: false,
    displayCart: false,
    displayCurrentFirm: false,
    displayDeliveryOptions: false,
    displayUserActions: false,
    displayProductSearch: false,
    displayLocationSearch: false,
    displayLanguagesSelector: false
  }
)

const initialSubheaderState = (): SubHeaderState => (
  {
    display: false,
    variant: 'default'
  }
)

const initialContentSideBarState = (): MainSideBarState => (
  {
    display: false,
    displayCart: false,
    displayDeliveryOptions: false,
    displayOrderSummary: false
  }
)

const initialFooterState = (): FooterState => (
  {
    display: false,
    displayInformations: false,
    displayLinks: false,
    displayGoToCartBtn: false,
    displayGoToCheckoutBtn: false
  }
)

const initialContentState = (): ContentState => (
  {
    displayNotification: false
  }
)

const initMobileNavigationState = (): MobileNavigationState => (
  {
    display: false
  }
)

export const initialState = (): LayoutState => ({
  header: initialHeaderState(),
  subHeader: initialSubheaderState(),
  mainSideBar: initialContentSideBarState(),
  content: initialContentState(),
  footer: initialFooterState(),
  mobileNavigation: initMobileNavigationState(),
  isMobile: false
})

export const LAYOUT_STATE = createState<LayoutState>({
  key,
  default: initialState()
})

export const IS_ON_MARKETPLACE_PAGE_SELECTOR = createSelector<boolean>({
  key: 'isOnMarketplacePageSelector',
  get: ({ get }) => {
    const isMarketplace = get(IS_MARKETPLACE_SELECTOR)
    const selectedShop = get(SELECTED_SHOP_STATE)

    if (!isMarketplace) {
      return false
    }

    return selectedShop === ''
  }
})

export const DOCUMENT_TITLE_SELECTOR = createSelector<string>({
  key: 'documentTitleSelector',
  get: ({ get }) => {
    const firm = get(FIRM_SELECTOR)

    return firm?.name || ''
  }
})

export const FAVICON_SELECTOR = createSelector<string>({
  key: 'faviconSelector',
  get: ({ get }) => {
    const firm = get(FIRM_SELECTOR)

    return firm?.assets.logo_square || ''
  }
})

export const META_TITLE_SELECTOR = createSelector<string>({
  key: 'metaTitleSelector',
  get: ({ get }) => {
    const firm = get(FIRM_SELECTOR)

    return firm?.name || ''
  }
})

export const META_DESCRIPTION_SELECTOR = createSelector<string>({
  key: 'metaDescriptionSelector',
  get: ({ get }) => {
    const firm = get(FIRM_SELECTOR)

    return firm?.info.find((info: Info) => info.content_type === 'firm_info')?.content
      || '' 
  }
})
